import { createApp } from "vue";

const popupContexts = {
    brand: require.context(
        "@/views/brand/popup",
        false
    ),
    investors: require.context(
        "@/views/investors/popup",
        false
    ),
    startup: require.context(
        "@/views/startup/popup",
        false
    ),
    inquiry: require.context(
        "@/views/inquiry/popup",
        false
    ),
    instore: require.context(
        "@/views/instore/popup",
        false
    ),
    policy: require.context(
        "@/views/policy/popup",
        false
    ),
    sustainability: require.context(
        "@/views/sustainability/popup",
        false
    )
};

const isAllowedPopupName = (popupName) => {
    if (!popupName) return false;

    return [...String(popupName)].every((char) => {
        const code = char.charCodeAt(0);
        const isNumber = code >= 48 && code <= 57;
        const isUpperAlpha = code >= 65 && code <= 90;
        const isLowerAlpha = code >= 97 && code <= 122;

        return isNumber || isUpperAlpha || isLowerAlpha || char === "_" || char === "-";
    });
};

const resolvePopupComponent = (triggerEl) => {
    const popupName = triggerEl.dataset.cont;
    if (!isAllowedPopupName(popupName)) {
        throw new Error(`Invalid popup component name: ${popupName}`);
    }

    const preferredRoot = triggerEl.dataset.popupRoot;
    const roots = preferredRoot
        ? [preferredRoot, ...Object.keys(popupContexts).filter((root) => root !== preferredRoot)]
        : Object.keys(popupContexts);

    for (const root of roots) {
        const context = popupContexts[root];
        if (!context) continue;

        try {
            return context(`./${popupName}.vue`).default;
        } catch (error) {
            // Try the next views popup directory.
            continue;
        }
    }

    throw new Error(`Popup component not found in views: ${popupName}`);
};

let popupApp = null;

const modal = {
    open(targetId, type = "default", triggerEl = null, popupProps = {}) {
        const target = document.getElementById(targetId);
        if (!target) return;

        target.setAttribute("modal-type", type);

        const activeModals = document.querySelectorAll(".modal_wrap.active");

        if (!target.classList.contains("active")) {
            target.classList.add("active");

            const zIdx = 100 + activeModals.length;
            target.style.zIndex = zIdx;

            let dim = document.querySelector(".modal_dim");

            if (dim) {
                dim.style.zIndex = zIdx - 1;
            } else if (!type.includes("no_dim")) {
                dim = document.createElement("div");
                dim.className = "modal_dim";
                dim.style.zIndex = zIdx - 1;
                document.body.appendChild(dim);
            }

            document.body.style.overflow = "hidden";
        }

        if (triggerEl && triggerEl.dataset.cont) {
            const container = target.querySelector(".modal_container");
            if (!container) return;

            container.replaceChildren();

            try {
                const component = resolvePopupComponent(triggerEl);

                const rootProps = popupProps && typeof popupProps === "object" ? popupProps : {};
                popupApp = createApp(component, rootProps);
                popupApp.mount(container);

            } catch (err) {
                // console.error("Popup load error:", err);
                alert("Failed to load popup.");
            }
        }
    },

    close(targetEl) {
        const target = targetEl.closest(".modal_wrap");
        if (!target) return;

        target.classList.remove("active");
        target.removeAttribute("modal-type");
        target.removeAttribute("style");

        if (popupApp) {
            popupApp.unmount();
            popupApp = null;
        }

        const activeModals = document.querySelectorAll(".modal_wrap.active");
        const dim = document.querySelector(".modal_dim");

        if (activeModals.length === 0 && dim) {
            dim.remove();
            document.body.style.overflow = "";
        } else if (dim) {
            const zIdx = 100 + activeModals.length;
            dim.style.zIndex = zIdx - 1;
        }
    },

    closeAll() {
        document.querySelectorAll(".modal_wrap.active").forEach((target) => {
            target.classList.remove("active");
            target.removeAttribute("modal-type");
            target.removeAttribute("style");
        });

        if (popupApp) {
            popupApp.unmount();
            popupApp = null;
        }

        const dim = document.querySelector(".modal_dim");
        if (dim) dim.remove();

        document.body.style.overflow = "";
    },

    toast(_type, _message, _time = 3000) {
        let _toast = document.querySelector(".toast");

        if (!_toast) {
            _toast = document.createElement("div");
            _toast.className = "toast";
            document.body.appendChild(_toast);
        }

        _toast.className = "toast active " + _type;
        _toast.replaceChildren();

        const message = document.createElement("span");
        message.textContent = _message;
        _toast.appendChild(message);

        if (_type === "auto") {
            setTimeout(() => {
                this.toast_close(_toast);
            }, _time);
        } else if (_type === "confirm") {
            const closeBtn = document.createElement("a");
            closeBtn.href = "#none";
            closeBtn.className = "btn_close";
            closeBtn.innerText = "close";
            closeBtn.onclick = () => this.toast_close(_toast);
            _toast.appendChild(closeBtn);

            setTimeout(() => {
                closeBtn.focus();
            }, 100);
        }
    },

    toast_close(el) {
        if (!el) return;
        el.classList.remove("active");
        setTimeout(() => {
            if (el.parentNode) el.parentNode.removeChild(el);
        }, 300);
    },
};

export default modal;
